import React, { useRef, useState, useEffect, useCallback } from 'react'
import { isAndroid } from 'react-device-detect'
import { Filter } from 'components/filters/useStreamFilters'
import BranchPullIndicator, {
    useFeedRidesPull,
    usePullChromeLinger,
} from 'components/panels/BranchPullIndicator'
import {
    DRAW_EXTENT,
    TOUCH_RELEASE_REFRESH_PULL,
    resolveTouchPull,
} from 'components/panels/branchPullGeometry'

declare global {
    interface Window {
        isDragging?: boolean
    }
}

const GLOBAL_DRAGGING_EVENT = 'knov:global-dragging'

// Constants for mobile pull-to-refresh behavior
export const TOP_TOLERANCE = 15 // Even more generous top area detection
export const POST_REFRESH_COOLDOWN = 300
export const VERTICAL_GESTURE_RATIO = 0.8 // Higher ratio for more strict vertical detection
export const MIN_SWIPE_DISTANCE = 5 // Minimum pixels to move before detecting direction
export const VERTICAL_ANGLE_THRESHOLD = 60 // Consider gesture vertical if angle is within ±60° of vertical

export interface MobilePullToRefreshProps {
    scrollContainerRef: React.RefObject<HTMLDivElement>
    onRefresh: () => void
    isRefreshing: boolean
    filter?: Filter // The filter object that determines if refresh is available
    showSpinner?: boolean
    disabled?: boolean // New prop to disable pull-to-refresh
    blockSelectors?: string[] // Optional: selectors that block PTR only when selection is active inside
}

// Animation helper for consistent animation behavior
const animatePullBackToStart = (
    startAmount: number,
    setPullProgress: (progress: number) => void,
    setIsPulling: (isPulling: boolean) => void,
    onComplete = () => {},
) => {
    // First, indicate we're no longer actively pulling
    setIsPulling(false)

    // Clear any existing animation timeouts
    const timeoutId = setTimeout(() => {
        // Set to 0 to let CSS handle the animation
        setPullProgress(0)

        const cleanupId = setTimeout(() => {
            onComplete()
        }, 400) // Longer timeout to ensure animation completes

        // Store the timeout ID to avoid leaks
        return () => clearTimeout(cleanupId)
    }, 20) // Slight delay to ensure state updates

    // Return cleanup function
    return () => clearTimeout(timeoutId)
}

const MobilePullToRefresh: React.FC<MobilePullToRefreshProps> = ({
    scrollContainerRef,
    onRefresh,
    isRefreshing,
    filter,
    showSpinner = true,
    disabled = false,
    blockSelectors = [],
}) => {
    const [isDragBlocked, setIsDragBlocked] = useState<boolean>(() => {
        try {
            return !!window.isDragging
        } catch {
            return false
        }
    })

    // Pull-to-refresh state
    const [isPulling, setIsPulling] = useState(false)
    const [pullProgress, setPullProgress] = useState(0)

    // The column rides the pull while the branch draws in the opened void.
    // Drag-block unmounts the indicator even mid-refresh, so the ride must
    // retract with it or the feed would sit displaced over an empty band.
    useFeedRidesPull(scrollContainerRef, {
        enabled: !isDragBlocked,
        isPulling,
        isRefreshing,
        pullProgress,
    })
    // Keeps the void's backdrop painted while the feed slides home.
    const pullChromeLingering = usePullChromeLinger(isPulling || isRefreshing)

    const touchStartY = useRef<number | null>(null)
    const touchStartX = useRef<number | null>(null)
    const pullStartedAtTopRef = useRef<boolean>(false)
    const lastRefreshTimeRef = useRef<number>(0)
    const gestureDirectionRef = useRef<'none' | 'vertical' | 'horizontal'>('none')
    const touchPointsHistory = useRef<{ x: number; y: number }[]>([])
    // Finger Y where the void is closed. Set when the pull begins, so a touch
    // that scrolled up to the top first does not jump the band open.
    const pullOriginYRef = useRef<number | null>(null)
    // Latest pull, readable by handlers without re-binding listeners each move.
    const pullRef = useRef(0)
    // One refresh per touch: after arming, the rest of the gesture is inert
    // even if the refresh finishes while the finger is still down.
    const gestureCommittedRef = useRef(false)

    // Animation frame reference for smooth animations
    const animationFrameRef = useRef<number | null>(null)

    // Helper functions to determine gesture direction
    const isGestureVertical = useCallback(
        (startX: number, startY: number, endX: number, endY: number) => {
            const deltaX = Math.abs(endX - startX)
            const deltaY = Math.abs(endY - startY)

            // Total movement must exceed minimum threshold
            const totalMovement = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
            if (totalMovement < MIN_SWIPE_DISTANCE) {
                return false
            }

            // Calculate angle from horizontal (in degrees)
            let angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI)

            // Determine if within vertical threshold
            return angle >= 90 - VERTICAL_ANGLE_THRESHOLD && angle <= 90 + VERTICAL_ANGLE_THRESHOLD
        },
        [],
    )

    // Helper to get the dominant direction from touch history
    const getDominantDirection = useCallback(() => {
        if (touchPointsHistory.current.length < 3 || !touchStartX.current || !touchStartY.current) {
            return 'none'
        }

        // Use the most recent point for more current direction
        const currentPoint = touchPointsHistory.current[touchPointsHistory.current.length - 1]

        // Check if primary movement is vertical using angle-based detection
        if (
            isGestureVertical(
                touchStartX.current,
                touchStartY.current,
                currentPoint.x,
                currentPoint.y,
            )
        ) {
            return 'vertical'
        }

        return 'horizontal'
    }, [isGestureVertical])

    // Helper function to check if a filter is valid/active
    const isFilterValid = useCallback(() => {
        if (disabled || isDragBlocked) return false
        if (filter === undefined) return true
        if (!filter) return false
        return true
    }, [filter, disabled, isDragBlocked])

    // Helper to check if we're in refresh cooldown
    const isInRefreshCooldown = useCallback(() => {
        return Date.now() - lastRefreshTimeRef.current < POST_REFRESH_COOLDOWN
    }, [])

    // Animation helper function for smooth pull progress updates
    const updatePullProgressSmoothly = useCallback(
        (targetValue: number) => {
            setPullProgress(targetValue)

            // Cancel any existing animation
            if (animationFrameRef.current !== null) {
                cancelAnimationFrame(animationFrameRef.current)
                animationFrameRef.current = null
            }
        },
        [], // No dependencies needed as it's just setting a value
    )

    // Helper to reset pull state with appropriate timing
    const resetPullState = useCallback(() => {
        // Force reset animation state completely
        setIsPulling(false)

        // Use timeout to ensure state updates
        setTimeout(() => {
            setPullProgress(0)

            // Force final reset after animation would be complete
            setTimeout(() => {
                pullStartedAtTopRef.current = false
                // Final failsafe reset
                setPullProgress(0)
                setIsPulling(false)
            }, 500)
        }, 20)
    }, [])

    // If a drag starts, immediately shut down PTR and reset UI state.
    useEffect(() => {
        if (!isDragBlocked) return
        try {
            // Cancel any ongoing animations
            if (animationFrameRef.current !== null) {
                cancelAnimationFrame(animationFrameRef.current)
                animationFrameRef.current = null
            }
        } catch {}

        // Hard reset pull indicators/gesture tracking
        touchStartY.current = null
        touchStartX.current = null
        touchPointsHistory.current = []
        gestureDirectionRef.current = 'none'
        pullStartedAtTopRef.current = false
        setIsPulling(false)
        setPullProgress(0)
    }, [isDragBlocked])

    // Listen for global dragging state (set by answers DnD provider).
    useEffect(() => {
        const handler = (e: Event) => {
            try {
                const ce = e as CustomEvent
                const next = !!ce?.detail?.isDragging
                setIsDragBlocked(next)
            } catch {
                // Fallback: read the global
                try {
                    setIsDragBlocked(!!window.isDragging)
                } catch {}
            }
        }

        try {
            window.addEventListener(GLOBAL_DRAGGING_EVENT, handler as EventListener)
        } catch {}

        return () => {
            try {
                window.removeEventListener(GLOBAL_DRAGGING_EVENT, handler as EventListener)
            } catch {}
        }
    }, [])

    // Handle refresh trigger
    const handleRefresh = useCallback(() => {
        if (isRefreshing || !isFilterValid() || isInRefreshCooldown()) {
            return
        }

        // Mark refresh time and reset tracking
        lastRefreshTimeRef.current = Date.now()
        gestureCommittedRef.current = true

        // The gesture has committed. Let the indicator and feed transition
        // together to the refreshing plateau instead of leaving the
        // no-transition pulling state active.
        setIsPulling(false)
        onRefresh()

        // Reset pull state immediately to avoid double animation
        pullStartedAtTopRef.current = false
    }, [onRefresh, isRefreshing, isFilterValid, isInRefreshCooldown])

    // Touch handlers
    const selectionActiveInside = (container: Element | null) => {
        try {
            if (!container) return false
            const sel = window.getSelection()
            if (!sel || sel.isCollapsed || sel.rangeCount === 0) return false
            const anchor = sel.anchorNode
            const node =
                anchor &&
                (anchor.nodeType === 1 ? (anchor as Element) : (anchor as Node)?.parentElement)
            return !!(node && container.contains(node))
        } catch (e) {
            return false
        }
    }

    const shouldIgnoreEvent = (target: EventTarget | null) => {
        try {
            const el = target as HTMLElement
            if (!el || typeof el.closest !== 'function') return false

            // Allow PTR by default; only block under specific conditions:
            // 1) Active selection inside title WithSelection
            const titleSelContainer = el.closest('.with-selection-comp.title-selection')
            if (titleSelContainer && selectionActiveInside(titleSelContainer)) return true

            // 2) Focus or selection inside NewAnswer editor
            const newAnswerContainer = el.closest('.common-editor-comp.new-answer')
            if (newAnswerContainer) {
                const active = document.activeElement
                const focusedInside = !!(
                    active &&
                    newAnswerContainer.contains(active) &&
                    ((active instanceof HTMLElement && active.isContentEditable) ||
                        (active instanceof Element && active.closest('.ProseMirror')))
                )
                if (focusedInside) return true
                if (selectionActiveInside(newAnswerContainer)) return true
            }

            // 3) Optional extra selectors: block only if selection is active inside them
            if (Array.isArray(blockSelectors) && blockSelectors.length > 0) {
                for (const sel of blockSelectors) {
                    const container = el.closest(sel)
                    if (container && selectionActiveInside(container)) return true
                }
            }

            return false
        } catch (e) {
            return false
        }
    }

    const onTouchStart = useCallback(
        (e: TouchEvent) => {
            const scrollContainer = scrollContainerRef.current
            if (!scrollContainer || isRefreshing || !isFilterValid() || isInRefreshCooldown()) {
                return
            }

            // Do not hijack touch events inside editors (to preserve iOS selection)
            if (shouldIgnoreEvent(e.target)) return

            // Check if we're at the top with tolerance
            const isAtTop = scrollContainer.scrollTop <= TOP_TOLERANCE

            gestureCommittedRef.current = false
            pullOriginYRef.current = null

            // Store touch position
            touchStartY.current = e.touches[0].clientY
            touchStartX.current = e.touches[0].clientX

            // Reset touch history and direction
            touchPointsHistory.current = [{ x: e.touches[0].clientX, y: e.touches[0].clientY }]
            gestureDirectionRef.current = 'none'

            // Mark if pull started at top
            pullStartedAtTopRef.current = isAtTop
        },
        [isFilterValid, isRefreshing, isInRefreshCooldown],
    )

    // The void follows the finger (resolveTouchPull) in both directions, like
    // the scroll it extends. Arming — the branch fully drawn — commits the
    // refresh mid-gesture; anything shorter waits for the release.
    const trackPull = useCallback(
        (e: TouchEvent, touchY: number) => {
            if (e.cancelable) e.preventDefault()
            if (gestureDirectionRef.current !== 'vertical') return

            if (pullOriginYRef.current === null) {
                pullOriginYRef.current = pullStartedAtTopRef.current
                    ? touchStartY.current ?? touchY
                    : touchY
                pullStartedAtTopRef.current = true
            }
            const pull = resolveTouchPull(touchY - pullOriginYRef.current)
            pullRef.current = pull

            if (pull <= 0) {
                if (isPulling) setIsPulling(false)
                setPullProgress(0)
                pullOriginYRef.current = null
                return
            }
            if (!isPulling) setIsPulling(true)
            setPullProgress(pull)

            if (pull >= DRAW_EXTENT) handleRefresh()
        },
        [isPulling, handleRefresh],
    )

    // Helper to handle touch events when not at top
    const handleNotAtTop = useCallback(() => {
        // No longer at top, cancel pull
        if (isPulling) {
            animatePullBackToStart(pullRef.current, updatePullProgressSmoothly, setIsPulling)
        }
        pullRef.current = 0
        pullOriginYRef.current = null
        pullStartedAtTopRef.current = false
    }, [isPulling, updatePullProgressSmoothly])

    // Helper to update touch history and detect movement direction
    const updateTouchHistoryAndDirection = useCallback(
        (touchX: number, touchY: number) => {
            // Add current point to history (limit to 5 points)
            touchPointsHistory.current.push({ x: touchX, y: touchY })
            if (touchPointsHistory.current.length > 5) {
                touchPointsHistory.current.shift()
            }

            // Calculate deltas for detection
            const deltaY = touchY - (touchStartY.current || 0)
            const deltaX = touchX - (touchStartX.current || 0)

            // Determine if this is the first significant movement
            if (gestureDirectionRef.current === 'none') {
                // Wait until we have enough movement to determine direction
                const totalMovement = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
                if (totalMovement > MIN_SWIPE_DISTANCE) {
                    gestureDirectionRef.current = getDominantDirection()
                }
            }

            return { deltaX, deltaY }
        },
        [getDominantDirection],
    )

    const onTouchMove = useCallback(
        (e: TouchEvent) => {
            const scrollContainer = scrollContainerRef.current
            if (!scrollContainer || isRefreshing || !isFilterValid() || isInRefreshCooldown()) {
                return
            }
            if (gestureCommittedRef.current) return

            // Do not hijack touch events inside editors (to preserve iOS selection)
            if (shouldIgnoreEvent(e.target)) return

            // A mid-feed touch cannot initiate pull-to-refresh. Avoid touch history, direction,
            // and scroll extent calculations on the normal scrolling path.
            if (!isPulling && scrollContainer.scrollTop > TOP_TOLERANCE) {
                pullStartedAtTopRef.current = false
                return
            }

            // Get current touch position
            const touchY = e.touches[0].clientY
            const touchX = e.touches[0].clientX

            // If we don't have a starting position, set it now
            if (touchStartY.current === null || touchStartX.current === null) {
                touchStartY.current = touchY
                touchStartX.current = touchX
                return
            }

            // Update touch history and get deltas
            const { deltaY } = updateTouchHistoryAndDirection(touchX, touchY)

            // Check if content is scrollable
            const isScrollable = scrollContainer.scrollHeight > scrollContainer.clientHeight
            // Only handle pull-down gestures at the top
            const isAtTop = scrollContainer.scrollTop <= TOP_TOLERANCE
            const isPullingDown = deltaY > 0

            // Always prevent default at top to prevent browser overscroll
            // Also prevent for non-scrollable content when pulling down
            if (
                isPulling ||
                (isAtTop && isPullingDown) ||
                (!isScrollable && isPullingDown && isAndroid)
            ) {
                trackPull(e, touchY)
            } else if (!isAtTop) {
                handleNotAtTop()
            }
        },
        [
            isRefreshing,
            isPulling,
            isFilterValid,
            isInRefreshCooldown,
            updateTouchHistoryAndDirection,
            trackPull,
            handleNotAtTop,
        ],
    )

    const onTouchEnd = useCallback(
        (e: TouchEvent) => {
            pullOriginYRef.current = null
            if (isRefreshing) return

            const pull = pullRef.current
            pullRef.current = 0
            if (isPulling && pull >= TOUCH_RELEASE_REFRESH_PULL) {
                handleRefresh()
                return
            }

            // If still pulling (didn't trigger refresh), animate back to start
            if (isPulling) {
                resetPullState()
            }
        },
        [isPulling, isRefreshing, handleRefresh, resetPullState],
    )

    // Set up event listeners
    useEffect(() => {
        const scrollContainer = scrollContainerRef.current
        if (!scrollContainer) return

        // Add touch event listeners - passive false only where needed
        scrollContainer.addEventListener('touchstart', onTouchStart, { passive: true })
        scrollContainer.addEventListener('touchmove', onTouchMove, { passive: false })
        scrollContainer.addEventListener('touchend', onTouchEnd, { passive: true })

        return () => {
            // Clean up event listeners
            scrollContainer.removeEventListener('touchstart', onTouchStart)
            scrollContainer.removeEventListener('touchmove', onTouchMove)
            scrollContainer.removeEventListener('touchend', onTouchEnd)

            // Cancel any ongoing animations
            if (animationFrameRef.current !== null) {
                cancelAnimationFrame(animationFrameRef.current)
            }
        }
    }, [onTouchStart, onTouchMove, onTouchEnd])

    // Prevent native browser pull-to-refresh on Android for questId panels or empty panels
    useEffect(() => {
        // Only apply this effect for Android and specific panel types
        const scrollContainer = scrollContainerRef.current
        const shouldPreventNativePull = isAndroid && (filter?.questId || !showSpinner)

        if (!scrollContainer || !shouldPreventNativePull) return

        // Use WeakMap to store touch positions without modifying the DOM element
        const touchStartMap = new WeakMap<HTMLElement, number>()

        // Store initial touch position
        const handleTouchStart = (e: TouchEvent) => {
            const touch = e.touches[0]
            if (touch && scrollContainer) {
                touchStartMap.set(scrollContainer, touch.clientY)
            }
        }

        // Prevent browser default for pull-down gestures
        const preventDefaultPullToRefresh = (e: TouchEvent) => {
            // Get touch position
            const touch = e.touches[0]
            if (!touch || !scrollContainer) return

            // Get initial touch position from WeakMap
            const startY = touchStartMap.get(scrollContainer)
            if (startY === undefined) return

            // Calculate direction (positive = pulling down)
            const deltaY = touch.clientY - startY
            const isPullingDown = deltaY > 0
            const isAtTopOrNotScrollable =
                scrollContainer.scrollTop <= 10 ||
                scrollContainer.scrollHeight <= scrollContainer.clientHeight

            // Only prevent default when pulling DOWN at the top or on non-scrollable content
            if (isPullingDown && isAtTopOrNotScrollable && e.cancelable) {
                e.preventDefault()
            }
        }

        // Reset touch position tracking
        const resetTouchStart = () => {
            if (scrollContainer) {
                touchStartMap.delete(scrollContainer)
            }
        }

        // Add touch event listeners
        scrollContainer.addEventListener('touchstart', handleTouchStart, { passive: true })
        scrollContainer.addEventListener('touchmove', preventDefaultPullToRefresh, {
            passive: false,
        })
        scrollContainer.addEventListener('touchend', resetTouchStart, { passive: true })
        scrollContainer.addEventListener('touchcancel', resetTouchStart, { passive: true })

        return () => {
            // Clean up event listeners
            scrollContainer.removeEventListener('touchstart', handleTouchStart)
            scrollContainer.removeEventListener('touchmove', preventDefaultPullToRefresh)
            scrollContainer.removeEventListener('touchend', resetTouchStart)
            scrollContainer.removeEventListener('touchcancel', resetTouchStart)
            if (scrollContainer) {
                touchStartMap.delete(scrollContainer)
            }
        }
    }, [filter?.questId, showSpinner])

    // Reset state when refresh completes. A finger still on the glass owns
    // the pull (isPulling), however long it holds.
    useEffect(() => {
        if (isRefreshing || isPulling || pullProgress <= 0) return
        const timeout = setTimeout(() => {
            pullStartedAtTopRef.current = false
            setPullProgress(0)
        }, 50)
        return () => clearTimeout(timeout)
    }, [isRefreshing, isPulling, pullProgress])

    if (isDragBlocked) return null
    if (!isPulling && !isRefreshing && !pullChromeLingering) return null

    return (
        <BranchPullIndicator
            isPulling={isPulling}
            pullProgress={pullProgress}
            isRefreshing={isRefreshing}
            showSpinner={showSpinner}
        />
    )
}

export default MobilePullToRefresh
